import { supportedCurrencies } from "@circle-react/config/Currencies";

import { t } from "@/i18n-js/instance";

import { FeatureRow } from "../FeatureRow";
import { usePlans } from "../PlansContext";
import { SectionWrapper } from "./SectionWrapper";

type PlanTier = "business" | "enterprise" | "plus";
type Product = "admin" | "headless";
type RequestType = "included" | "extra";

const includedRequests = {
  admin: {
    business: "5,000",
    enterprise: "30,000",
    plus: "30,000",
  },
  headless: {
    business: "100",
    enterprise: "500",
    plus: "500",
  },
} as const;

const getApiRequestPrice = (
  // [TODO][PlansPageRedesign][BeforeRelease] Pass this via Internal API
  planTier: PlanTier,
  currency: string | null,
  product: Product,
  requestType: RequestType,
): string => {
  const currencyKey = (currency || "usd").toLowerCase();

  if (requestType === "included") {
    if (product === "admin") {
      return `${includedRequests[product][planTier]} ${t("pricing_plans.interval.abbreviation.month")}`;
    }
    return `${includedRequests[product][planTier]} ${t("settings.developers.mau")}`;
  }

  // Extra requests pricing
  if (product === "admin") {
    const prices = {
      [supportedCurrencies.USD.code.toLowerCase()]: {
        business: 0.005,
        enterprise: 0.002,
        plus: 0.002,
        symbol: supportedCurrencies.USD.symbol,
      },
      [supportedCurrencies.BRL.code.toLowerCase()]: {
        business: 0.03,
        enterprise: 0.012,
        plus: 0.012,
        symbol: supportedCurrencies.BRL.symbol,
      },
    } as const;

    const price = prices[currencyKey][planTier];
    const symbol = prices[currencyKey].symbol;
    return `${symbol}${price} / ${t("pricing_plans.request")}`;
  }

  // headless pricing
  const prices = {
    [supportedCurrencies.USD.code.toLowerCase()]: {
      business: 0.5,
      enterprise: 0.3,
      plus: 0.3,
      symbol: supportedCurrencies.USD.symbol,
    },
    [supportedCurrencies.BRL.code.toLowerCase()]: {
      business: 3.5,
      enterprise: 2,
      plus: 2,
      symbol: supportedCurrencies.BRL.symbol,
    },
  } as const;

  const price = prices[currencyKey][planTier];
  const symbol = prices[currencyKey].symbol;
  return `${symbol}${price} / ${t("pricing_plans.mau")}`;
};

export const ApiAddons = () => {
  const { billingCurrency } = usePlans();

  return (
    <SectionWrapper sectionName={t("pricing_plans.section.api")}>
      <FeatureRow
        title={t("pricing_plans.feature.included_admin_api")}
        basic={false}
        professional={false}
        business={getApiRequestPrice(
          "business",
          billingCurrency,
          "admin",
          "included",
        )}
        enterprise={getApiRequestPrice(
          "enterprise",
          billingCurrency,
          "admin",
          "included",
        )}
        plus={getApiRequestPrice("plus", billingCurrency, "admin", "included")}
      />
      <FeatureRow
        title={t("pricing_plans.feature.extra_admin_api")}
        basic={false}
        professional={false}
        business={getApiRequestPrice(
          "business",
          billingCurrency,
          "admin",
          "extra",
        )}
        enterprise={getApiRequestPrice(
          "enterprise",
          billingCurrency,
          "admin",
          "extra",
        )}
        plus={getApiRequestPrice("plus", billingCurrency, "admin", "extra")}
      />
      <FeatureRow
        title={t("pricing_plans.feature.included_headless_api")}
        basic={false}
        professional={false}
        business={getApiRequestPrice(
          "business",
          billingCurrency,
          "headless",
          "included",
        )}
        enterprise={getApiRequestPrice(
          "enterprise",
          billingCurrency,
          "headless",
          "included",
        )}
        plus={getApiRequestPrice(
          "plus",
          billingCurrency,
          "headless",
          "included",
        )}
      />
      <FeatureRow
        title={t("pricing_plans.feature.extra_headless_api")}
        basic={false}
        professional={false}
        business={getApiRequestPrice(
          "business",
          billingCurrency,
          "headless",
          "extra",
        )}
        enterprise={getApiRequestPrice(
          "enterprise",
          billingCurrency,
          "headless",
          "extra",
        )}
        plus={getApiRequestPrice("plus", billingCurrency, "headless", "extra")}
      />
    </SectionWrapper>
  );
};
